import { CSSProperties, useEffect, useRef, useState } from 'react';

/**
 * EternityWordmark — the ETERNITY title as crisp DIN outlines (from
 * public/eternity-wordmark.svg, text converted to paths, so it renders
 * identically on every device regardless of installed fonts).
 *
 * The crossbar is drawn as a <rect> INSIDE the SVG coordinate space, locked to
 * the exact midbar geometry of the artwork (y 33.326, height 10.5 — i.e. the
 * E's middle arm), so it stays pixel-aligned at every rendered size. It extends
 * far past the letters and the SVG uses overflow:visible, so the host section's
 * overflow:hidden clips it to a clean full-bleed edge.
 *
 * The bar sits ON TOP of the letters in one continuous colour, so the E's
 * middle arms (which coincide with the bar) literally become the line — the
 * letters read as threaded by it, with no outline/gap to separate them.
 *
 * Letters span x 346–654 of the original 1000-wide viewBox → viewBox
 * "346 0 308 78" here.
 */

/* The E's middle arm spans y 33.325–43.815 (height 10.49). The bar is grown a
   touch past it on both sides so it fully covers the arm with no white sliver
   from anti-aliasing, while staying centred on the same midline (38.57). */
const BAR_Y = 32.88;
const BAR_H = 11.4;
const FAR = 6000; // half-extent of the full-bleed bar (clipped by the host)

const LETTERS = (
    <g fillRule="nonzero" transform="matrix(1,0,0,1,0,-461.098873)">
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M431.445,988.249L431.445,691.582L558.112,691.582L558.112,731.582L473.945,731.582L473.945,818.665L547.278,818.665L547.278,858.665L473.945,858.665L473.945,945.749L558.112,945.749L558.112,988.249L431.445,988.249Z" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M613.528,988.249L613.528,731.582L564.362,731.582L564.362,691.582L705.195,691.582L705.195,731.582L656.028,731.582L656.028,988.249L613.528,988.249Z" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M723.945,988.249L723.945,691.582L850.612,691.582L850.612,731.582L766.445,731.582L766.445,818.665L839.778,818.665L839.778,858.665L766.445,858.665L766.445,945.749L850.612,945.749L850.612,988.249L723.945,988.249Z" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M920.612,731.582L920.612,824.082L944.778,824.082C952.278,824.082 958.251,823.04 962.695,820.957C967.14,818.874 970.612,815.888 973.112,811.999C975.334,808.11 976.862,803.318 977.695,797.624C978.528,791.929 978.945,785.332 978.945,777.832C978.945,770.332 978.528,763.735 977.695,758.04C976.862,752.346 975.195,747.415 972.695,743.249C967.417,735.471 957.417,731.582 942.695,731.582L920.612,731.582ZM878.112,988.249L878.112,691.582L946.445,691.582C996.445,691.582 1021.445,720.61 1021.445,778.665C1021.445,796.165 1018.737,811.027 1013.32,823.249C1007.903,835.471 998.39,845.332 984.778,852.832L1030.612,988.249L985.612,988.249L946.028,861.582L920.612,861.582L920.612,988.249L878.112,988.249Z" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M1053.945,988.249L1053.945,691.582L1094.778,691.582L1158.945,870.332L1159.778,870.332L1159.778,691.582L1202.278,691.582L1202.278,988.249L1162.278,988.249L1097.278,809.915L1096.445,809.915L1096.445,988.249L1053.945,988.249Z" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <rect x="1241.862" y="691.582" width="42.5" height="296.667" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M1353.528,988.249L1353.528,731.582L1304.362,731.582L1304.362,691.582L1445.195,691.582L1445.195,731.582L1396.028,731.582L1396.028,988.249L1353.528,988.249Z" />
        </g>
        <g transform="matrix(0.262255,0,0,0.262255,233.049939,279.728159)">
            <path d="M1503.528,988.249L1503.528,862.832L1445.195,691.582L1490.195,691.582L1524.362,809.499L1525.195,809.499L1559.362,691.582L1604.362,691.582L1546.028,862.832L1546.028,988.249L1503.528,988.249Z" />
        </g>
    </g>
);

interface EternityWordmarkProps {
    /** Heading element to render (visually hidden, for SEO/screen readers). */
    as?: 'h1' | 'h2';
    /** Bar colour for this placement (CSS colour). */
    barColor?: string;
    /** Letter colour. Defaults to white (all current placements are dark). */
    inkColor?: string;
    /** Extra class for sizing/positioning the wordmark from the parent. */
    className?: string;
    style?: CSSProperties;
    /** Easter egg: 5 clicks flash this line through the title, then it decays
     *  back (Cyberpunk 2077 thumbprint homage). When omitted, the egg is off. */
    secret?: string;
}

const EternityWordmark = ({
    as: Heading = 'h2',
    barColor = 'var(--et-cyan)',
    inkColor = '#fff',
    className = '',
    style,
    secret,
}: EternityWordmarkProps) => {
    const [revealing, setRevealing] = useState(false);
    const hits = useRef(0);

    useEffect(() => {
        if (!revealing) return;
        const t = window.setTimeout(() => setRevealing(false), 1500);
        return () => window.clearTimeout(t);
    }, [revealing]);

    const onClick = () => {
        if (!secret || revealing) return;
        hits.current += 1;
        if (hits.current >= 5) {
            hits.current = 0;
            setRevealing(true);
        }
    };

    return (
        <div
            className={`et-wm ${secret ? 'et-wm-secret-on' : ''} ${revealing ? 'revealing' : ''} ${className}`}
            style={{ ['--wm-bar' as string]: barColor, color: inkColor, ...style }}
            onClick={onClick}
        >
            <Heading className="et-wm-sr">ETERNITY</Heading>
            {secret && (
                <span className="et-wm-secret" aria-hidden="true">
                    <span className="et-wm-secret-box" data-text={secret}>{secret}</span>
                </span>
            )}
            <svg
                className="et-wm-svg"
                viewBox="346 0 308 78"
                aria-hidden="true"
                preserveAspectRatio="xMidYMid meet"
            >
                {/* letters */}
                <g fill="currentColor">{LETTERS}</g>

                {/* full-bleed bar, on top — the E's middle arms become the line */}
                <rect
                    x={-FAR}
                    y={BAR_Y}
                    width={FAR * 2}
                    height={BAR_H}
                    fill="var(--wm-bar, var(--et-cyan))"
                />
            </svg>

            <style>{`
                .et-wm {
                    position: relative;
                    color: var(--wm-ink, #fff);
                }
                .et-wm-svg {
                    display: block;
                    width: 100%;
                    height: auto;
                    overflow: visible;
                }
                .et-wm-sr {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    margin: 0;
                    padding: 0;
                    overflow: hidden;
                    clip: rect(0 0 0 0);
                    white-space: nowrap;
                    border: 0;
                }

                /* ── 5-click memory easter egg — a HUD readout surfaces ──── */
                .et-wm-secret {
                    position: absolute;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    pointer-events: none;
                }
                .et-wm-secret-box {
                    font-family: var(--font-mono);
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.22em;
                    font-size: clamp(0.58rem, 1.9vw, 1.05rem);
                    line-height: 1;
                    color: var(--wm-bar, var(--et-cyan));
                    background: rgba(5, 6, 15, 0.86);
                    border: 1px solid var(--wm-bar, var(--et-cyan));
                    padding: 0.85em 1.2em;
                    white-space: nowrap;
                }
                /* fade the whole wordmark (letters + bar) to a faint ghost so the
                   readout reads cleanly over it */
                .et-wm.revealing .et-wm-svg {
                    opacity: 0.07;
                    transition: opacity 0.18s ease;
                }
                .et-wm.revealing .et-wm-secret {
                    animation: et-wm-memory 1.5s ease 1;
                }
                .et-wm.revealing .et-wm-secret-box {
                    animation: et-wm-glitch 1.5s steps(1) 1;
                }
                @keyframes et-wm-memory {
                    0% { opacity: 0; }
                    9% { opacity: 1; }
                    16% { opacity: 0.25; }
                    24% { opacity: 1; }
                    84% { opacity: 1; }
                    100% { opacity: 0; }
                }
                @keyframes et-wm-glitch {
                    0%, 100% { transform: none; text-shadow: none; }
                    11% { transform: translateX(-2px); text-shadow: 2px 0 #dc2626, -2px 0 var(--et-cyan); }
                    19% { transform: translateX(2px); text-shadow: -2px 0 #dc2626, 2px 0 var(--et-cyan); }
                    27% { transform: none; text-shadow: none; }
                    88% { transform: translateX(1px); text-shadow: 1px 0 #dc2626; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .et-wm.revealing .et-wm-secret { animation: et-wm-memory-rm 1.5s ease 1; }
                    .et-wm.revealing .et-wm-secret-box { animation: none; }
                    @keyframes et-wm-memory-rm {
                        0%, 100% { opacity: 0; }
                        15%, 85% { opacity: 1; }
                    }
                }
            `}</style>
        </div>
    );
};

export default EternityWordmark;
